"use client";

import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { 
  BsBrowserChrome, 
  BsGithub, 
  BsYoutube, 
  BsEye, 
  BsSearch,
  BsChevronLeft,
  BsChevronRight,
  BsChevronDoubleLeft,
  BsChevronDoubleRight
} from "react-icons/bs";
import "./projects.css";
import { projects } from "../../data/projects-data";

import Nav from "../nav/Nav";
import Footer from "../footer/Footer";

const Projects = () => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredProjects, setFilteredProjects] = useState(projects);
  const [isSearching, setIsSearching] = useState(false);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [projectsPerPage, setProjectsPerPage] = useState(6);
  const [showPagination, setShowPagination] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Debounce function for search
  const debounce = (func, wait) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  };

  // Search function with improved logic
  const performSearch = useCallback((term) => {
    if (!term.trim()) {
      setFilteredProjects(projects);
      setIsSearching(false);
      setCurrentPage(1); // Reset to first page when clearing search
      return;
    }

    setIsSearching(true);
    
    const searchTermLower = term.toLowerCase().trim();
    const filtered = projects.filter((project) => {
      // Search in title
      const titleMatch = project.title.toLowerCase().includes(searchTermLower);
      
      // Search in stack technologies
      const stackMatch = project.stack.some(tech => 
        tech.toLowerCase().includes(searchTermLower)
      );
      
      // Search in description (optional)
      const descriptionMatch = t(`portfolio.descriptions.${project.description}`)
        .toLowerCase()
        .includes(searchTermLower);
      
      return titleMatch || stackMatch || descriptionMatch;
    });
    
    setFilteredProjects(filtered);
    setCurrentPage(1); // Reset to first page when searching
    setIsSearching(false);
  }, [t]);

  // Debounced search
  const debouncedSearch = useCallback(
    debounce(performSearch, 300),
    [performSearch]
  );

  useEffect(() => {
    debouncedSearch(searchTerm);
  }, [searchTerm, debouncedSearch]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    
    // Validación mejorada: permitir letras, números, espacios, guiones, puntos y acentos
    const validPattern = /^[a-zA-ZÀ-ÿ0-9\s\-_\.]+$/;
    
    if (value === "" || validPattern.test(value)) {
      setSearchTerm(value);
    }
  };

  const clearSearch = () => {
    setSearchTerm("");
    setFilteredProjects(projects);
    setCurrentPage(1);
  };

  // Pagination calculations
  const totalProjects = filteredProjects.length;
  const totalPages = Math.ceil(totalProjects / projectsPerPage);
  const startIndex = (currentPage - 1) * projectsPerPage;
  const endIndex = startIndex + projectsPerPage;
  const currentProjects = filteredProjects.slice(startIndex, endIndex);

  // Generate page numbers with ellipsis for better UX
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show smart pagination with ellipsis
      if (currentPage <= 3) {
        // Near the beginning
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        // Near the end
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // In the middle
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  // Pagination handlers
  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const goToFirstPage = () => goToPage(1);
  const goToLastPage = () => goToPage(totalPages);
  const goToPreviousPage = () => goToPage(currentPage - 1);
  const goToNextPage = () => goToPage(currentPage + 1);

  const handleProjectsPerPageChange = (e) => {
    const newPerPage = parseInt(e.target.value);
    setProjectsPerPage(newPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  // Show pagination only if there are more projects than items per page
  useEffect(() => {
    setShowPagination(totalProjects > projectsPerPage);
  }, [totalProjects, projectsPerPage]);

  // Highlight search term in text
  const highlightText = (text, searchTerm) => {
    if (!searchTerm.trim()) return text;
    
    const regex = new RegExp(`(${searchTerm})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? (
        <mark key={index} className="highlight">{part}</mark>
      ) : (
        part
      )
    );
  };

  return (
    <>
      <Nav />
      <section id="all-projects" className="section-padding">
        <h2>{t("projects.title")}</h2>
        
        {/* Search Input */}
        <div className="search-container">
          <div className="search-input-wrapper">
            <BsSearch className={`search-icon ${isSearching ? 'searching' : ''}`} />
            <input
              type="text"
              placeholder={t("projects.searchPlaceholder") || "Buscar por título o tecnología..."}
              value={searchTerm}
              onChange={handleSearchChange}
              className="search-input"
              maxLength={50}
              aria-label="Buscar proyectos"
            />
            {searchTerm && (
              <button 
                onClick={clearSearch}
                className="clear-search-btn"
                title="Limpiar búsqueda"
                aria-label="Limpiar búsqueda"
              >
                ×
              </button>
            )}
          </div>
          {searchTerm && (
            <div className="search-results-info">
              {isSearching ? (
                <p className="searching-text">
                  {t("projects.searching") || "Buscando..."}
                </p>
              ) : filteredProjects.length === 0 ? (
                <p className="no-results">
                  {t("projects.noResults") || "No se encontraron proyectos que coincidan con tu búsqueda."}
                </p>
              ) : (
                <p className="results-count">
                  {t("projects.resultsFound", { count: filteredProjects.length }) || 
                   `${filteredProjects.length} proyecto${filteredProjects.length !== 1 ? 's' : ''} encontrado${filteredProjects.length !== 1 ? 's' : ''}`}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Projects Per Page Selector */}
        {totalProjects > 0 && (
          <div className="projects-per-page-container">
            <div className="projects-per-page">
              <label htmlFor="projectsPerPage">
                {t("projects.projectsPerPage") || "Proyectos por página:"}
              </label>
              <select
                id="projectsPerPage"
                value={projectsPerPage}
                onChange={handleProjectsPerPageChange}
                className="projects-per-page-select"
              >
                <option value={3}>3</option>
                <option value={6}>6</option>
                <option value={9}>9</option>
                <option value={12}>12</option>
                <option value={15}>15</option>
              </select>
            </div>
          </div>
        )}

        <div className="container projects__container">
          {currentProjects.map(
            ({ id, image, title, description, github, demo, video, stack }) => {
              return (
                <article key={id} className="projects__item">
                  <h3 className="projects__item-title">
                    {searchTerm ? highlightText(title, searchTerm) : title}
                  </h3>
                  <div className="projects__item-image">
                    <img src={image || "/placeholder.svg"} alt="projects" />
                  </div>
                  <p>{t(`portfolio.descriptions.${description}`)}</p>
                  <div className="projects__tech-stack">
                    {stack &&
                      stack.map((tech, index) => (
                        <span 
                          key={index} 
                          className={`tech-tag ${searchTerm && tech.toLowerCase().includes(searchTerm.toLowerCase()) ? 'highlighted' : ''}`}
                        >
                          {searchTerm ? highlightText(tech, searchTerm) : tech}
                        </span>
                      ))}
                  </div>
                  <div className="projects__item-cta">
                    <Link
                      key={id}
                      className="btn"
                      to={`/project/${id}`}
                      title="Ver Detalhes"
                    >
                      <BsEye className="btn__icon" />
                    </Link>
                    <a
                      className="btn"
                      href={github}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <BsGithub className="btn__icon" />
                    </a>
                    <a
                      className="btn"
                      href={demo}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <BsBrowserChrome className="btn__icon" />
                    </a>
                    <a
                      className="btn"
                      href={video}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <BsYoutube className="btn__icon" />
                    </a>
                  </div>
                </article>
              );
            }
          )}
        </div>

        {/* Pagination Info */}
        {showPagination && totalProjects > 0 && (
          <div className="pagination-info-container">
            <div className="pagination-info">
              <span>
                {t("projects.showing", { 
                  from: startIndex + 1, 
                  to: Math.min(endIndex, totalProjects), 
                  total: totalProjects 
                }) || `Mostrando ${startIndex + 1}-${Math.min(endIndex, totalProjects)} de ${totalProjects} proyectos`}
              </span>
            </div>
          </div>
        )}

        {/* Pagination Controls */}
        {showPagination && totalProjects > 0 && (
          <div className="pagination-controls">
            <button
              onClick={goToFirstPage}
              disabled={currentPage === 1}
              className="pagination-btn"
              title={t("projects.firstPage") || "Primera página"}
              aria-label={t("projects.firstPage") || "Primera página"}
            >
              <BsChevronDoubleLeft />
            </button>
            
            <button
              onClick={goToPreviousPage}
              disabled={currentPage === 1}
              className="pagination-btn"
              title={t("projects.previousPage") || "Página anterior"}
              aria-label={t("projects.previousPage") || "Página anterior"}
            >
              <BsChevronLeft />
            </button>
            
            <div className="page-numbers">
              {getPageNumbers().map((page, index) => (
                page === '...' ? (
                  <span key={index} className="page-ellipsis">
                    ...
                  </span>
                ) : (
                  <button
                    key={index}
                    onClick={() => goToPage(page)}
                    className={`page-number ${currentPage === page ? 'active' : ''}`}
                    aria-label={`Página ${page}`}
                  >
                    {page}
                  </button>
                )
              ))}
            </div>
            
            <button
              onClick={goToNextPage}
              disabled={currentPage === totalPages}
              className="pagination-btn"
              title={t("projects.nextPage") || "Página siguiente"}
              aria-label={t("projects.nextPage") || "Página siguiente"}
            >
              <BsChevronRight />
            </button>
            
            <button
              onClick={goToLastPage}
              disabled={currentPage === totalPages}
              className="pagination-btn"
              title={t("projects.lastPage") || "Última página"}
              aria-label={t("projects.lastPage") || "Última página"}
            >
              <BsChevronDoubleRight />
            </button>
          </div>
        )}
      </section>
      <Footer />
    </>
  );
};

export default Projects;
